// Wohler Chemical — Gabapentinoids / α2δ Ligands (Program L)
// New category added per user request 2025-01
//
// Scheduling notes:
//   Gabapentin: federally unscheduled; some states (KY, TN, VA, MI) classify Schedule V or require prescription monitoring
//   Pregabalin: Schedule V (US federal)
//   Mirogabalin: approved in Japan (Tarlige); not approved in US
//   Atagabalin / Imagabalin / 4-methylpregabalin: research compounds
//   Gabapentin enacarbil (Horizant): FDA-approved prodrug
//
// References:
//   Bian F. et al. Br. J. Pharmacol. 2006, 147, 1–4 (α2δ mechanism)
//   Field M.J. et al. Proc. Natl. Acad. Sci. 2006, 103, 17537 (α2δ-1 vs α2δ-2 selectivity)
//   Kitano Y. et al. Pain 2016, 157, 1972 (mirogabalin)
//   Taylor C.P. et al. Epilepsy Res. 2007, 73, 137 (pregabalin mechanism)

const stub = (extra) => ({
  id: extra.id,
  name: extra.name,
  fullName: extra.fullName || extra.name,
  aliases: extra.aliases || [],
  categoryId: extra.categoryId,
  program: extra.program,
  cas: extra.cas || "Research compound",
  formula: extra.formula || "—",
  molecularWeight: extra.molecularWeight || "—",
  schedulingStatus: extra.schedulingStatus || "unscheduled-federal",
  schedulingNote: extra.schedulingNote || "Verify federal and state scheduling before any handling.",
  benefitScore: extra.benefitScore || 65,
  harmScore: extra.harmScore || 32,
  evidenceConfidence: extra.evidenceConfidence || "MEDIUM",
  status: extra.status || "Research-Needed",
  statusColor: extra.statusColor || "blue",
  goDecision: extra.goDecision || "Research-needed — category entry pending full profile",
  priorityTier: extra.priorityTier || "Tier 2 — α2δ Program",
  summary: extra.summary,
  pros: extra.pros || ["α2δ calcium channel modulator — non-opioid, non-benzodiazepine mechanism"],
  cons: extra.cons || ["Full profile pending — see flagship 4-AcO-MET page for target structure"],
  pharmacology: extra.pharmacology || null,
  labSynthesis: extra.labSynthesis || null,
  kgScaleSynthesis: extra.kgScaleSynthesis || null,
  safety: extra.safety || null,
  wohlerScoring: extra.wohlerScoring || null,
  references: extra.references || [],
  nextSteps: extra.nextSteps || ["Populate full research profile using Wohler flagship compound template"],
  flag: extra.flag || null
});

export const gabapentinoidCompounds = {

  // —————————————————————————————————————————————
  // GABAPENTIN — the baseline comparator (full profile)
  // —————————————————————————————————————————————
  "gabapentin": {
    id: "gabapentin",
    name: "Gabapentin",
    fullName: "1-(Aminomethyl)cyclohexaneacetic acid",
    aliases: ["Neurontin", "Gralise"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "60142-96-3",
    formula: "C9H17NO2",
    molecularWeight: "171.24 g/mol",
    schedulingStatus: "unscheduled-federal (state monitoring)",
    schedulingNote: "Federally unscheduled. Several states (Kentucky, Tennessee, Virginia, Michigan, North Dakota, West Virginia) have classified gabapentin as Schedule V or added it to their prescription monitoring programs. FDA-approved for post-herpetic neuralgia and partial seizures; extensive off-label use for neuropathic pain and anxiety-adjacent conditions.",
    benefitScore: 75,
    harmScore: 30,
    evidenceConfidence: "HIGH",
    status: "Reference / Comparator",
    statusColor: "blue",
    goDecision: "REFERENCE — baseline α2δ comparator for Wohler Program L",
    priorityTier: "Reference — α2δ baseline",
    summary: "Gabapentin is the foundational α2δ-1/α2δ-2 auxiliary calcium-channel ligand. FDA-approved for partial seizures and post-herpetic neuralgia with extensive evidence-base use in neuropathic pain, fibromyalgia, restless legs syndrome, and anxiety. Used by Wohler as the Program L reference compound for all α2δ analog SAR and biomarker studies.",
    pros: [
      "FDA-approved — extensive human safety and efficacy data",
      "Non-opioid, non-benzodiazepine mechanism — valuable harm-reduction framing",
      "Low abuse liability vs pregabalin (despite state-level monitoring)",
      "Renal excretion only (no CYP metabolism) — minimal drug-drug interactions",
      "Simple, scalable manufacturing with standard solvents"
    ],
    cons: [
      "Non-linear oral bioavailability (saturable L-amino acid transporter)",
      "Sedation, dizziness, peripheral edema at therapeutic doses",
      "Respiratory depression risk when combined with opioids (2019 FDA warning)",
      "State-level controlled substance status in several jurisdictions",
      "Withdrawal syndrome after chronic dosing"
    ],
    pharmacology: {
      mechanismOfAction: "Binds to the α2δ-1 and α2δ-2 auxiliary subunits of voltage-gated calcium channels (CaV2.1, CaV2.2). This binding reduces trafficking of the channel complex to the presynaptic terminal and reduces presynaptic calcium influx upon depolarization, thereby decreasing release of excitatory neurotransmitters (glutamate, substance P, CGRP). Despite the 'gaba' in the name, gabapentin does NOT directly bind GABA-A or GABA-B receptors.",
      targets: [
        { receptor: "α2δ-1 (CACNA2D1)", activity: "High-affinity ligand", value: "Ki ≈ 38 nM — analgesia driver" },
        { receptor: "α2δ-2 (CACNA2D2)", activity: "High-affinity ligand", value: "Ki ≈ 150 nM — CNS side effect driver" },
        { receptor: "GABA-A / GABA-B", activity: "No binding", value: "Ki > 100 μM — despite name" },
        { receptor: "Glutamate receptors", activity: "No direct binding", value: "Indirect effect via reduced presynaptic Ca²⁺/glutamate release" },
        { receptor: "L-amino acid transporter (LAT1)", activity: "Substrate", value: "Saturable — explains dose-nonlinear PK" }
      ],
      onsetDuration: "Oral: onset 1–2 h; tmax 2–3 h; t½ 5–7 h; duration 6–8 h. Saturable absorption at doses >1200 mg — higher single doses show reduced fractional bioavailability.",
      metabolism: "Negligible hepatic metabolism. >95% excreted unchanged in urine. Renal clearance proportional to creatinine clearance — dose adjustment required in renal impairment (CrCl <60 mL/min)."
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis (100 g target)",
      overview: "Four-step synthesis from 1,1-cyclohexanediacetic acid: (1) monoamide formation, (2) Hoffmann rearrangement to primary amine, (3) hydrolysis to amino-acid, (4) HCl salt crystallization. Standard Wohler solvents throughout.",
      steps: [
        { step: "1", title: "Cyclohexanediacetic acid monoamide", description: "Dissolve 1,1-cyclohexanediacetic acid (50 g) in THF with CDI (1.05 eq), then add aqueous NH3. Stir RT 12 h. Concentrate, crystallize from EtOH.", solvent: "THF / EtOH", keyReagents: "CDI, NH3" },
        { step: "2", title: "Hoffmann rearrangement", description: "Treat monoamide with NaOCl (2 eq) + NaOH (3 eq) in H2O/IPA (1:1) at 55–60 °C, 4 h. Acidify carefully to pH 4 with 1M HCl. Extract with EtOAc. Dry, concentrate.", solvent: "H2O / IPA / EtOAc", keyReagents: "NaOCl, NaOH" },
        { step: "3", title: "Purification", description: "Ion-exchange purification on cation-exchange resin (Dowex 50WX8). Elute amino acid with aqueous ammonia. Concentrate to give gabapentin free base.", solvent: "H2O / aq. NH3", keyReagents: "Dowex 50WX8" },
        { step: "4", title: "HCl salt crystallization", description: "Dissolve gabapentin in hot EtOH, add 1.02 eq 37% HCl. Cool slowly to crystallize. Filter, wash with cold EtOH/Acetone, dry under vacuum.", solvent: "EtOH / Acetone", keyReagents: "HCl" }
      ],
      analytics: "1H/13C NMR, HPLC-ELSD (amino-acid HPLC), HRMS, titratable N"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Manufacturing — Gabapentin HCl",
      feasibility: "HIGH — commodity API, many CMOs produce kg-scale routinely",
      overview: "Wohler internal kg-scale synthesis is practical but commercially competitive — gabapentin API is available at $80–150/kg from qualified generics suppliers (India, China). For research-scale needs, Wohler sources from qualified supplier with USP/Ph.Eur. release testing. Internal synthesis capability maintained as strategic backup.",
      targetBatch: "10 kg gabapentin HCl per campaign (research reference standard grade)",
      batchPlan: {
        targetBatch: "10 kg gabapentin HCl",
        startingMaterial: "12 kg 1,1-cyclohexanediacetic acid",
        keyVolumes: [
          "THF: 100 L (step 1)",
          "CDI: 12 kg (step 1)",
          "NaOCl (12.5% aq): 50 L (step 2)",
          "EtOAc: 150 L (extractions)",
          "Dowex 50WX8 resin: 30 L (step 3)",
          "EtOH: 60 L (crystallization)"
        ]
      },
      solventsUsed: ["THF", "EtOAc", "IPA", "EtOH", "Acetone", "Water"],
      standardSolventsNotUsed: ["DCM not required", "DMF not required"],
      equipment: [
        "500 L glass-lined reactor (steps 1, 2)",
        "Ion-exchange column (100 L bed volume)",
        "200 L crystallization vessel",
        "40 L Nutsche filter",
        "Vacuum tray dryer"
      ],
      procedure: [
        "Step 1 — Charge 1,1-cyclohexanediacetic acid (12 kg), THF (80 L), CDI (12 kg) to 500 L reactor. Stir 1 h RT. Add 28% aqueous NH3 (15 L) slowly. Stir 12 h RT. Concentrate, crystallize monoamide from EtOH.",
        "Step 2 — Suspend monoamide in H2O/IPA (1:1, 80 L). Add NaOH (6 kg) and 12.5% NaOCl (50 L) over 2 h at 55 °C. Stir 4 h. Cool, acidify carefully to pH 4 with 6M HCl. Extract with EtOAc (3 × 40 L). Dry, concentrate.",
        "Step 3 — Load crude gabapentin onto Dowex 50WX8 (H+ form, 30 L bed). Wash with H2O. Elute with 1M aqueous NH3. Concentrate to solid free base.",
        "Step 4 — Dissolve free base in hot EtOH (50 L), add 37% HCl (1.02 eq). Cool slowly 70 °C → 5 °C over 12 h. Filter, wash with cold EtOH/Acetone, dry under vacuum at 45 °C to constant weight.",
        "Release testing: USP/Ph.Eur. monograph compliance, HPLC ≥99.5%, residual solvents, heavy metals, Karl Fischer, melting point."
      ],
      yield: "55–65% overall across 4 steps",
      hazards: [
        "NaOCl + strong base: exothermic; controlled addition and cooling required",
        "CDI: moisture-sensitive — dry THF and N2 atmosphere",
        "Final API is low-potency — standard PPE (Band 2) sufficient"
      ],
      vendorNotes: [
        "For reference-standard needs: purchase USP-grade gabapentin HCl from qualified supplier ($150–300/kg) — internal synthesis not cost-competitive",
        "1,1-cyclohexanediacetic acid: Sigma, TCI",
        "Research lots: Cerilliant or USP reference standard"
      ],
      estCostPerKg: "Internal synthesis: ~$800–1,200/kg. Purchased USP grade: $150–300/kg. Wohler default: purchase commercial reference material unless IP/supply chain strategic reason."
    },
    safety: {
      acuteToxicity: "Acute oral LD50 (rat) > 8000 mg/kg — very low acute toxicity. No single-dose lethality at pharmacological doses.",
      chronicRisks: "Sedation, dizziness, ataxia, peripheral edema, weight gain, cognitive slowing at therapeutic doses. Respiratory depression risk in combination with opioids (FDA 2019 safety warning). Withdrawal syndrome (anxiety, insomnia, nausea) after chronic daily dosing — taper required.",
      sideEffects: [
        "Sedation, dizziness (common, dose-dependent)",
        "Ataxia, peripheral edema",
        "Weight gain (5–10 kg with chronic dosing)",
        "Cognitive slowing (esp. elderly)",
        "Respiratory depression with opioid/sedative combinations"
      ],
      contraindications: [
        "Concurrent opioid or sedative use requires monitoring",
        "Severe renal impairment (dose adjustment per CrCl)",
        "Children <3 years (partial seizure indication only)"
      ],
      ehsClassification: "GHS not classified (very low hazard). Containment Band 2.",
      ppe: ["Standard lab PPE — gloves, glasses, coat"]
    },
    wohlerScoring: {
      methodology: "Wohler Scoring v1.0",
      benefit: 75,
      benefitBreakdown: { therapeuticRationale: 17, evidenceQuality: 19, unmetMedicalNeed: 15, novelty: 4, universityInterest: 20 },
      harm: 30,
      harmBreakdown: { acuteToxicity: 2, chronicToxicity: 8, dependence: 5, schedulingRisk: 10, ehsRisk: 5 },
      net: 45,
      evidenceConfidence: "HIGH",
      confidenceRationale: "HIGH — FDA-approved with decades of clinical data",
      rationale: "Gabapentin serves as the α2δ reference compound for Wohler Program L. Research value is as a comparator, not a novel development candidate."
    },
    nextSteps: [
      "Maintain 100 g reference stock (USP-grade purchased)",
      "Use as primary comparator in all α2δ analog SAR studies",
      "Benchmark α2δ-1 vs α2δ-2 selectivity of Wohler analogs vs gabapentin"
    ]
  },

  // —————————————————————————————————————————————
  // PREGABALIN — Schedule V comparator
  // —————————————————————————————————————————————
  "pregabalin": stub({
    id: "pregabalin",
    name: "Pregabalin",
    fullName: "(S)-3-(Aminomethyl)-5-methylhexanoic acid",
    aliases: ["Lyrica"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "148553-50-8",
    formula: "C8H17NO2",
    molecularWeight: "159.23 g/mol",
    schedulingStatus: "Schedule V (US federal)",
    schedulingNote: "Schedule V CSA. FDA-approved for neuropathic pain, fibromyalgia, partial seizures, generalized anxiety disorder (in EU).",
    benefitScore: 78,
    harmScore: 38,
    evidenceConfidence: "HIGH",
    status: "Reference / Comparator",
    statusColor: "blue",
    goDecision: "REFERENCE — Schedule V α2δ comparator",
    priorityTier: "Reference — α2δ baseline (Schedule V)",
    summary: "Pregabalin is a Schedule V α2δ ligand with 5–10× higher potency than gabapentin and linear oral PK. FDA-approved for neuropathic pain, fibromyalgia, partial seizures, and (EU) generalized anxiety disorder. Schedule V status reflects documented abuse/dependence liability, especially in combination with opioids.",
    pros: [
      "5–10× more potent than gabapentin at α2δ-1/-2",
      "Linear, predictable oral PK (no saturable absorption)",
      "FDA-approved with extensive clinical data",
      "Stronger anxiety-adjacent efficacy than gabapentin"
    ],
    cons: [
      "Schedule V — requires DEA registration, controlled handling",
      "Higher abuse liability than gabapentin",
      "Stronger respiratory depression risk with opioid combinations",
      "Weight gain, edema, sedation more pronounced"
    ],
    flag: "Schedule V - DEA registration required"
  }),

  // —————————————————————————————————————————————
  // MIROGABALIN — Japan-approved
  // —————————————————————————————————————————————
  "mirogabalin": stub({
    id: "mirogabalin",
    name: "Mirogabalin",
    fullName: "(1R,5S,6S)-6-(Aminomethyl)-3-ethylbicyclo[3.2.0]hept-3-ene-6-carboxylic acid",
    aliases: ["Tarlige", "DS-5565"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "1138245-13-2",
    formula: "C12H19NO2",
    molecularWeight: "209.28 g/mol",
    schedulingStatus: "Not scheduled in US (not approved)",
    schedulingNote: "Approved in Japan (2019, Tarlige) for peripheral neuropathic pain. Not FDA-approved; not currently scheduled in the US.",
    benefitScore: 72,
    harmScore: 30,
    evidenceConfidence: "MEDIUM-HIGH",
    status: "Priority 2",
    statusColor: "green",
    goDecision: "GO — α2δ-1 selective SAR target",
    priorityTier: "Tier 2 — Active SAR",
    summary: "Mirogabalin is a newer α2δ ligand (Daiichi Sankyo) with moderate α2δ-1 selectivity (~16× vs α2δ-2) — hypothesis-testing for whether α2δ-1 selectivity preserves analgesia while reducing α2δ-2-driven CNS side effects. Approved in Japan for peripheral neuropathic pain. Key Wohler interest: template for α2δ-1-selective analog design.",
    pros: [
      "α2δ-1 selective (~16×) vs α2δ-2 — reduced sedation hypothesis",
      "Japan-approved with Phase 3 efficacy in diabetic peripheral neuropathic pain",
      "Unscheduled in US — research-access advantage",
      "Bicyclic scaffold provides distinct SAR vs linear gabapentinoids"
    ],
    cons: [
      "Complex stereochemistry (3 stereocenters)",
      "Multi-step synthesis with chiral resolution",
      "Limited long-term human safety data vs pregabalin",
      "Still has dose-dependent sedation despite α2δ-1 selectivity"
    ],
    pharmacology: {
      mechanismOfAction: "Selective binding to α2δ-1 (~16× vs α2δ-2) of voltage-gated calcium channels; reduces presynaptic Ca²⁺ influx and excitatory neurotransmitter release.",
      targets: [
        { receptor: "α2δ-1", activity: "High-affinity selective ligand", value: "Ki ≈ 13.5 nM" },
        { receptor: "α2δ-2", activity: "Lower affinity", value: "Ki ≈ 220 nM (16× selectivity)" }
      ],
      onsetDuration: "Oral: tmax 1 h; t½ 2.5–3 h; duration 6–8 h",
      metabolism: "Primarily renal excretion unchanged"
    },
    nextSteps: [
      "Source mirogabalin reference standard (Japanese supplier or custom synthesis)",
      "Use as α2δ-1 selectivity benchmark for Wohler analog library",
      "Design 20-compound α2δ-1-selective library around bicyclic scaffold"
    ],
    flag: "Approved in Japan only - import research license required"
  }),

  // —————————————————————————————————————————————
  // ATAGABALIN — investigational
  // —————————————————————————————————————————————
  "atagabalin": stub({
    id: "atagabalin",
    name: "Atagabalin",
    fullName: "Atagabalin (PD-200,390)",
    aliases: ["PD-200390"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "274691-27-7",
    formula: "C8H15NO2",
    molecularWeight: "157.21 g/mol",
    schedulingStatus: "Unscheduled (investigational)",
    schedulingNote: "Investigational compound; not approved or scheduled.",
    benefitScore: 62,
    harmScore: 32,
    evidenceConfidence: "MEDIUM",
    status: "Research-Needed",
    statusColor: "blue",
    goDecision: "Research-needed — α2δ SAR analog",
    priorityTier: "Tier 2 — SAR expansion",
    summary: "Atagabalin (Pfizer/Parke-Davis PD-200,390) is an investigational α2δ ligand studied for insomnia, anxiety, and pain. Development was discontinued but the compound remains a useful SAR reference. α2δ binding profile similar to pregabalin but with distinct PK."
  }),

  // —————————————————————————————————————————————
  // IMAGABALIN — GAD investigational
  // —————————————————————————————————————————————
  "imagabalin": stub({
    id: "imagabalin",
    name: "Imagabalin",
    fullName: "(3R,5R)-3-Amino-5-methyloctanoic acid (PD-332,334)",
    aliases: ["PD-332334"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "487045-56-3",
    formula: "C9H19NO2",
    molecularWeight: "173.25 g/mol",
    schedulingStatus: "Unscheduled (investigational)",
    schedulingNote: "Investigational compound; Pfizer Phase 2 for generalized anxiety disorder. Not scheduled.",
    benefitScore: 65,
    harmScore: 32,
    evidenceConfidence: "MEDIUM",
    status: "Research-Needed",
    statusColor: "blue",
    goDecision: "Research-needed — GAD/anxiety α2δ candidate",
    priorityTier: "Tier 2 — Anxiety α2δ",
    summary: "Imagabalin is an investigational α2δ ligand (Pfizer PD-332,334) studied in Phase 2 for generalized anxiety disorder. Research interest: α2δ-mediated anxiolysis without benzodiazepine-class dependence."
  }),

  // —————————————————————————————————————————————
  // GABAPENTIN ENACARBIL — FDA-approved prodrug
  // —————————————————————————————————————————————
  "gabapentin-enacarbil": stub({
    id: "gabapentin-enacarbil",
    name: "Gabapentin Enacarbil",
    fullName: "Gabapentin enacarbil (XP13512) — acyloxyalkyl carbamate prodrug of gabapentin",
    aliases: ["XP13512", "Horizant"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "478296-72-9",
    formula: "C16H27NO6",
    molecularWeight: "329.39 g/mol",
    schedulingStatus: "Unscheduled-federal (Horizant is Rx)",
    schedulingNote: "FDA-approved (Horizant) for restless legs syndrome and post-herpetic neuralgia. Prescription-only but not scheduled.",
    benefitScore: 68,
    harmScore: 28,
    evidenceConfidence: "HIGH",
    status: "Reference — Prodrug",
    statusColor: "blue",
    goDecision: "REFERENCE — prodrug strategy exemplar",
    priorityTier: "Reference — Prodrug design template",
    summary: "Gabapentin enacarbil (Horizant) is an FDA-approved acyloxyalkyl carbamate prodrug of gabapentin designed to overcome gabapentin's saturable L-amino-acid transporter absorption. Absorbed via high-capacity MCT1 transporter → hydrolyzed to gabapentin. Strong Wohler research interest as a prodrug design template for other α2δ analogs.",
    flag: "Prodrug — hydrolyzes to gabapentin in vivo"
  }),

  // —————————————————————————————————————————————
  // 4-METHYLPREGABALIN — SAR analog
  // —————————————————————————————————————————————
  "4-methylpregabalin": stub({
    id: "4-methylpregabalin",
    name: "4-Methylpregabalin",
    fullName: "(3S,4R)-3-(Aminomethyl)-4,5-dimethylhexanoic acid",
    aliases: [],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "Research compound",
    formula: "C9H19NO2",
    molecularWeight: "173.25 g/mol",
    schedulingStatus: "Research compound (structural analog of Schedule V pregabalin)",
    schedulingNote: "Structural analog of pregabalin — Federal Analogue Act scrutiny possible. Verify state and federal status before synthesis or handling.",
    benefitScore: 58,
    harmScore: 40,
    evidenceConfidence: "LOW",
    status: "Research-Needed",
    statusColor: "orange",
    goDecision: "CAUTION — analog-act scrutiny + dependence profile",
    priorityTier: "Tier 3 — SAR reference",
    summary: "4-Methylpregabalin is a structural SAR analog of pregabalin with additional methyl substitution. Research interest is narrow (α2δ SAR exploration). Analog Act scrutiny and pregabalin-like dependence concerns require careful legal and pharmacological review before any Wohler work.",
    flag: "Pregabalin analog — Analog Act risk + dependence concern"
  }),

  // —————————————————————————————————————————————
  // α2δ-1 SELECTIVE CLASS — research target class
  // —————————————————————————————————————————————
  "alpha2delta-1-selective-class": stub({
    id: "alpha2delta-1-selective-class",
    name: "α2δ-1 Selective Ligands",
    fullName: "α2δ-1 subunit-selective voltage-gated calcium channel ligands (research class)",
    aliases: ["α2δ-1 selective"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "Research class",
    formula: "Various",
    molecularWeight: "Various",
    schedulingStatus: "Unscheduled (research class)",
    schedulingNote: "Research class of α2δ-1-selective compounds. Specific analogs require individual scheduling review.",
    benefitScore: 78,
    harmScore: 28,
    evidenceConfidence: "MEDIUM",
    status: "Priority 1 — Target Class",
    statusColor: "green",
    goDecision: "GO — flagship Wohler α2δ-1-selective SAR program",
    priorityTier: "Tier 1 — α2δ-1 Selective Program",
    summary: "The α2δ-1 selective ligand class is Wohler's flagship Program L target. Central hypothesis: α2δ-1 binding drives analgesia, while α2δ-2 binding drives sedation/cognitive side effects. Selective α2δ-1 ligands (>50× vs α2δ-2) should provide equivalent analgesic efficacy with reduced sedation, ataxia, and cognitive burden. Mirogabalin is the closest approved example (~16× selectivity) — Wohler targets ≥50× selectivity for next-gen analogs.",
    pros: [
      "Separates analgesia (α2δ-1) from sedation/ataxia (α2δ-2)",
      "Clean mechanism — non-opioid, non-benzodiazepine",
      "Strong IP landscape (mirogabalin validates α2δ-1 selectivity concept)",
      "Phase 3 clinical validation via mirogabalin approval",
      "High university-research appeal (pain, neurology, sensory biology)"
    ],
    cons: [
      "SAR for >50× α2δ-1:α2δ-2 selectivity is challenging",
      "Complex stereochemistry often required",
      "Limited long-term safety data for selective analogs",
      "Still dose-dependent sedation observed in mirogabalin despite selectivity"
    ],
    nextSteps: [
      "Design 30-compound α2δ-1 selective library using mirogabalin and gabapentin as anchors",
      "Primary screen: α2δ-1 binding (Kd), α2δ-2 counter-screen",
      "Target: ≥50× α2δ-1:α2δ-2 selectivity",
      "In vivo: Spinal nerve ligation (SNL) pain model + rotarod for CNS side effects",
      "Lead candidate advancement to GLP tox and Pre-IND"
    ]
  }),

  // —————————————————————————————————————————————
  // PERIPHERAL α2δ LIGANDS — research class
  // —————————————————————————————————————————————
  "peripheral-alpha2delta-class": stub({
    id: "peripheral-alpha2delta-class",
    name: "Peripheral α2δ Ligands",
    fullName: "Peripherally restricted α2δ calcium-channel ligands (research class)",
    aliases: ["Peripheral α2δ"],
    categoryId: "gabapentinoids",
    program: "Program L — α2δ Calcium-Channel Ligands",
    cas: "Research class",
    formula: "Various",
    molecularWeight: "Various",
    schedulingStatus: "Unscheduled (research class)",
    schedulingNote: "Research class. BBB-excluded α2δ ligands target peripheral sensory neurons without CNS side effects.",
    benefitScore: 72,
    harmScore: 22,
    evidenceConfidence: "MEDIUM",
    status: "Priority 1 — Target Class",
    statusColor: "green",
    goDecision: "GO — peripheral α2δ analgesia program",
    priorityTier: "Tier 1 — Peripheral α2δ Program",
    summary: "Peripherally restricted α2δ ligands target nociceptor α2δ subunits in the dorsal root ganglion and peripheral sensory terminals without BBB penetration. Rationale: much of gabapentin/pregabalin's CNS side effects (sedation, ataxia, cognitive slowing) derive from central α2δ binding, while peripheral α2δ binding delivers the analgesic effect. Peripheral restriction (via high polarity or P-gp efflux) decouples efficacy from CNS side effects. Parallels the peripheral KOR strategy (Program D) as a non-controlled, non-CNS-side-effect analgesic class.",
    pros: [
      "Eliminates sedation, ataxia, cognitive slowing of central α2δ binding",
      "Non-controlled, non-opioid, non-benzodiazepine mechanism",
      "Lower abuse liability than pregabalin",
      "Parallels validated peripheral KOR strategy (difelikefalin/Korsuva)",
      "Cleaner safety in elderly and polypharmacy populations"
    ],
    cons: [
      "Peripheral restriction (high polarity / P-gp substrate) often hurts oral bioavailability",
      "α2δ is ubiquitously expressed — peripheral selectivity is partial at best",
      "Limited literature precedent vs peripheral KOR",
      "Analgesic efficacy may be more modest than central α2δ ligands"
    ],
    nextSteps: [
      "Literature review: identify polar α2δ ligand scaffolds with low logP and high PSA",
      "Design 20-compound library: α2δ-1 + peripheral restriction (MDCK-MDR1, PAMPA assays)",
      "Primary screen: α2δ-1 binding, brain/plasma ratio in rodent",
      "In vivo: SNL neuropathic pain + rotarod (CNS sparing)",
      "Lead candidate: 14-day rat tox + cardiovascular safety",
      "Target indication: non-controlled chronic neuropathic pain"
    ]
  })

};